import { DataState } from '@delen/bd-data-states';
import {
  EmptyFeatureResult,
  patchState,
  signalStoreFeature,
  SignalStoreFeature,
  withMethods,
  withState,
  WritableStateSource,
} from '@ngrx/signals';

export type DataStateSlice = {
  dataState: DataState;
};

export type NamedDataStateSlice<Collection extends string> = {
  [K in keyof DataStateSlice as Collection extends ''
    ? `${Collection}${K}`
    : `${Collection}${Capitalize<K>}`]: DataStateSlice[K];
};

// Extract keys ending with 'DataState'
export type KeysEndingWith<T, Suffix extends string> = Extract<
  {
    [K in keyof T]: K extends string ? (K extends `${string}${Suffix}` ? K : never) : never;
  }[keyof T],
  string
>;

// Get the base name by removing the 'DataState' suffix
export type BaseNameFromKey<K extends string, Suffix extends string> = K extends `${infer Base}${Suffix}`
  ? Base
  : never;

// Create a union type of all base names
export type BaseDataStateKeys<State> = {
  [K in KeysEndingWith<State, 'DataState'>]: BaseNameFromKey<K, 'DataState'>;
}[KeysEndingWith<State, 'DataState'>];

export type WithBdDataStateMethods<State> = {
  setDataState: (nextDataState: DataState, collections?: BaseDataStateKeys<State>[]) => void;
  resetDataState: (collection?: BaseDataStateKeys<State>) => void;
};

export function withBdDataStates<State, StateProperty extends string = keyof State & string>(
  stateProperties: StateProperty[],
): SignalStoreFeature<
  EmptyFeatureResult,
  EmptyFeatureResult & {
    state: NamedDataStateSlice<StateProperty>;
    methods: WithBdDataStateMethods<NamedDataStateSlice<StateProperty>>;
  }
>;

export function withBdDataStates(): SignalStoreFeature<
  EmptyFeatureResult,
  EmptyFeatureResult & {
    state: DataStateSlice;
    methods: WithBdDataStateMethods<DataStateSlice>;
  }
>;

export function withBdDataStates<State extends Record<string, unknown>>(
  stateProperties?: (keyof State & string)[],
): SignalStoreFeature {
  const statePropsList: string[] = stateProperties ?? [];

  return signalStoreFeature(
    withState(() => {
      if (statePropsList.length) {
        return statePropsList.reduce(
          (acc, stateProp) => ({
            ...acc,
            [stateProp + 'DataState']: DataState.Initial,
          }),
          {},
        );
      }
      return { dataState: DataState.Initial };
    }),
    withMethods((store: Record<string, unknown> & WritableStateSource<object>) => {
      return {
        setDataState: (nextDataState: DataState, properties?: string[]) => {
          if (properties) {
            properties.forEach((basePropertyName) => {
              // Convert base name to property name with 'DataState' suffix
              const fullPropertyName = `${basePropertyName}DataState`;

              // Check if this property exists in the store
              if (fullPropertyName in store) {
                patchState(store, () => ({
                  [fullPropertyName]: nextDataState,
                }));
              }
            });
          } else {
            // Default case when no collections specified
            patchState(store, () => ({
              dataState: nextDataState,
            }));
          }
        },
        resetDataState: (basePropertyName?: string) => {
          if (!basePropertyName) {
            patchState(store, () => ({
              dataState: DataState.Initial,
            }));
          } else {
            const fullPropertyName = `${basePropertyName}DataState`;
            if (fullPropertyName in store) {
              patchState(store, () => ({
                [fullPropertyName]: DataState.Initial,
              }));
            }
          }
        },
      };
    }),
  );
}
